// Journey registry — the single index of every purchase funnel.
//
// Adding an insurance vertical is: write one config file, add one line here.
// Routes, stepper, left-rail copy, forms, validation and navigation all derive
// from that config, so there is no second place to register a product.

import travel from "./travel";
import pet from "./pet";
import motor from "./motor";
import life from "./life";
import { assertJourney } from "./validate";

/** @typedef {import("../../form/types").JourneyConfig} JourneyConfig */
/** @typedef {import("../../form/types").StepConfig} StepConfig */
/** @typedef {import("../../form/types").NavigationConfig} NavigationConfig */

/** @type {Record<string, JourneyConfig>} */
export const JOURNEYS = {};

/**
 * Register (or replace) a journey at runtime.
 *
 * The built-in verticals are registered below from their JS modules, but the
 * config is plain data, so the same call accepts a parsed JSON document — a
 * journey served by the backend, or one edited by an admin tool. Structural
 * mistakes (a step with no path, a summary naming an unknown field) throw here,
 * at registration, rather than surfacing as a blank screen.
 *
 * @param {JourneyConfig} journey
 * @returns {JourneyConfig}
 */
export const registerJourney = (journey) => {
  assertJourney(journey);
  JOURNEYS[journey.key] = journey;
  return journey;
};

/**
 * Register several journeys from a JSON document: either an array of journeys
 * or an object keyed by journey key.
 *
 * @param {JourneyConfig[] | Record<string, JourneyConfig> | string} json
 */
export const loadJourneys = (json) => {
  const parsed = typeof json === "string" ? JSON.parse(json) : json;
  const list = Array.isArray(parsed) ? parsed : Object.values(parsed || {});
  return list.map(registerJourney);
};

[travel, pet, motor, life].forEach(registerJourney);

export const journeyList = () => Object.values(JOURNEYS);

/** @param {string} key */
export const getJourney = (key) => JOURNEYS[key] || null;

/**
 * Normalise however a product is named elsewhere in the app to a journey key.
 *
 * Redux stores `insuranceType` as a display string ("Motor Insurance") and the
 * backend uses yet another casing ("MOTOR INSURANCE"), while journey configs
 * are keyed "motor". Rather than make every caller remember which spelling it
 * holds, everything funnels through here.
 *
 * @param {string} value
 * @returns {string | null}
 */
export const journeyKeyFor = (value) => {
  if (!value) return null;
  const slug = String(value).toLowerCase().replace(/[^a-z]/g, "");
  if (JOURNEYS[slug]) return slug;
  // "motorinsurance" / "travelinsurance" / "petinsurance" / "lifeinsurance"
  const key = Object.keys(JOURNEYS).find((k) => slug.startsWith(k));
  return key || null;
};

/** An alias entry, normalised: `"/x"` and `{ path: "/x", screen }` both work. */
const aliasOf = (alias) => (typeof alias === "string" ? { path: alias } : alias);

/** Every route a step answers to — its canonical path plus any aliases. */
export const pathsOf = (step) => [step.path, ...(step.aliasPaths || []).map((a) => aliasOf(a).path)];

/**
 * Every route a step owns, with the screen each one renders.
 *
 * A step with a `form` and no `screen` renders the generic form step. A step
 * with a bespoke body names its screen; an alias may name its own (the compare
 * view of a plan list), or inherit the step's.
 *
 * @param {StepConfig} step
 * @returns {Array<{ path: string, screen: string | null, screenProps: Record<string, *> }>}
 */
export const routesOf = (step) => [
  { path: step.path, screen: step.screen || null, screenProps: step.screenProps || {} },
  ...(step.aliasPaths || []).map((alias) => {
    const a = aliasOf(alias);
    return {
      path: a.path,
      screen: a.screen || step.screen || null,
      screenProps: a.screenProps || step.screenProps || {},
    };
  }),
];

/**
 * Every route in every journey — the landing page plus each step's routes —
 * deduplicated so a path shared by several journeys (document upload) is
 * mounted once. This is what App.js renders; nothing lists funnel routes by
 * hand.
 *
 * @returns {Array<{ path: string, screen: string | null, screenProps: Record<string, *>, journey: string }>}
 */
export const allRoutes = () => {
  const seen = new Set();
  const out = [];
  for (const journey of journeyList()) {
    const routes = [
      ...(journey.landing ? [{ path: journey.entry, screen: journey.landing, screenProps: {} }] : []),
      ...journey.steps.flatMap(routesOf),
    ];
    for (const route of routes) {
      if (seen.has(route.path)) continue;
      seen.add(route.path);
      out.push({ ...route, journey: journey.key });
    }
  }
  return out;
};

/** Is this path a journey's landing page? */
export const isEntryPath = (pathname) => journeyList().some((j) => j.entry === pathname);

/**
 * The browser tab title for a path, or null when no journey owns it.
 *
 * @param {string} pathname
 * @param {string} [preferredJourney]
 */
export const titleFor = (pathname, preferredJourney) => {
  const landing = journeyList().find((j) => j.entry === pathname);
  if (landing) return `SmartSure - ${landing.label}`;
  const match = findStepByPath(pathname, preferredJourney);
  if (!match) return null;
  return `SmartSure - ${match.step.title || match.step.label}`;
};

/**
 * Locate the journey + step that owns a pathname.
 *
 * Shared screens (e.g. /document-upload, used by both Travel and Motor) appear
 * in more than one journey, so `preferredJourney` — the product the user is
 * actually buying, from redux — disambiguates. Without it we fall back to the
 * first match, which keeps the stepper working on a cold deep-link.
 *
 * @param {string} pathname
 * @param {string} [preferredJourney]
 * @returns {{ journey: JourneyConfig, step: StepConfig, index: number } | null}
 */
export const findStepByPath = (pathname, preferredJourney) => {
  const search = (journey) => {
    const index = journey.steps.findIndex((s) => pathsOf(s).includes(pathname));
    return index === -1 ? null : { journey, step: journey.steps[index], index };
  };

  const preferredKey = journeyKeyFor(preferredJourney);
  if (preferredKey) {
    const hit = search(JOURNEYS[preferredKey]);
    if (hit) return hit;
  }
  for (const journey of journeyList()) {
    const hit = search(journey);
    if (hit) return hit;
  }
  return null;
};

/** @returns {StepConfig | null} */
export const stepAt = (journeyKey, index) => {
  const journey = getJourney(journeyKey);
  if (!journey) return null;
  return journey.steps[index] || null;
};

/**
 * The route to move forward to, or null on the last step.
 *
 * Order in the `steps` array is the funnel order. A step may override its
 * destination with `next` — the last intake step of a vertical with no live
 * quote yet hands off to a holding page rather than dead-ending.
 */
export const nextPath = (journeyKey, index) => {
  const step = stepAt(journeyKey, index);
  if (step?.next) return step.next;
  return stepAt(journeyKey, index + 1)?.path ?? null;
};

/** @type {Required<NavigationConfig>} */
const DEFAULT_NAVIGATION = { placement: "both", nextHint: true };

/**
 * Forward-navigation settings for a step: defaults, then the journey's, then
 * the step's own.
 *
 * @returns {Required<NavigationConfig>}
 */
export const navigationFor = (journey, step) => ({
  ...DEFAULT_NAVIGATION,
  ...(journey?.navigation || {}),
  ...(step?.navigation || {}),
});

/**
 * Rail config for a step: journey-level defaults merged with the step's own
 * overrides, so shared copy (the bullets, the process-flow image) is written
 * once per vertical.
 */
export const railFor = (journey, step) => ({ ...(journey.rail || {}), ...(step.rail || {}) });

